import { API_BASE_URL } from "../config/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import EngineerLayout from "../components/EngineerLayout";

const getGeolocation = () => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      console.warn("Geolocation is not supported by this browser.");
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (error) => {
        console.error("Error acquiring geolocation:", error);
        resolve(null);
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  });
};

const formatAddress = (address) => {
  if (!address) return "-";
  if (typeof address === "string") return address;

  return [address.street, address.area, address.city, address.state, address.pincode]
    .filter(Boolean)
    .join(", ");
};

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString("en-GB") : "--";

const getComplaintLabel = (travel) =>
  travel?.complaintRefId || travel?.complaintId || "Complaint not entered";

const statusBadgeColor = (status) => {
  switch (status) {
    case "Started":
      return "bg-blue-500";
    case "At Site":
      return "bg-amber-500";
    case "Left Site":
      return "bg-violet-500";
    case "Completed":
      return "bg-emerald-600";
    default:
      return "bg-slate-400";
  }
};

const getNextAction = (travel) => {
  const status = travel?.status || "Not Started";

  if (status === "Not Started") {
    return { label: "Start Journey", status: "Started", field: "startTime" };
  }
  if (status === "Started") {
    return { label: "Reached Site", status: "At Site", field: "reachSiteTime" };
  }
  if (status === "At Site") {
    return { label: "Left Site", status: "Left Site", field: "leaveSiteTime" };
  }
  if (status === "Left Site") {
    return { label: "Reached Home", status: "Completed", field: "reachHomeTime" };
  }

  return null;
};

export default function TravelUpdate() {
  const engineerName = localStorage.getItem("engineerName") || "";
  const engineerEmail = localStorage.getItem("engineerEmail") || "";
  const engineerId = localStorage.getItem("engineerId") || "";
  const engineerLookup =
    engineerId ||
    engineerEmail ||
    engineerName;

  const [travel, setTravel] = useState(null);
  const [travelHistory, setTravelHistory] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedComplaintId, setSelectedComplaintId] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const engineerMatches = useMemo(
    () =>
      [engineerName, engineerEmail, engineerId, engineerLookup]
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean),
    [engineerEmail, engineerId, engineerLookup, engineerName]
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const [travelRes, historyRes, complaintsRes, customersRes] = await Promise.all([
        axios.get(API_BASE_URL + `/api/travel/engineer/${engineerLookup}`),
        axios.get(API_BASE_URL + `/api/travel/engineer/${engineerLookup}/history`),
        axios.get(API_BASE_URL + "/api/complaints"),
        axios.get(API_BASE_URL + "/api/customers")
      ]);

      const latestTravel = travelRes.data && travelRes.data.entryType === "complaint"
        ? { ...travelRes.data, journey: travelRes.data.journey || {} }
        : null;

      setTravel(latestTravel);
      setTravelHistory(
        (Array.isArray(historyRes.data) ? historyRes.data : []).filter((item) => item.entryType === "complaint")
      );
      setComplaints(
        (Array.isArray(complaintsRes.data) ? complaintsRes.data : []).filter((complaint) =>
          engineerMatches.includes(String(complaint.engineerAssigned || "").trim().toLowerCase())
        )
      );
      setCustomers(Array.isArray(customersRes.data) ? customersRes.data : []);
    } catch (error) {
      console.error("Error fetching travel data:", error);
      setTravel(null);
      setTravelHistory([]);
      setComplaints([]);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [engineerLookup, engineerMatches]);

  useEffect(() => {
    if (!engineerLookup) return;
    fetchData();
  }, [engineerLookup, fetchData]);

  const customerMap = useMemo(
    () => new Map(customers.map((customer) => [String(customer._id), customer])),
    [customers]
  );

  const complaintOptions = useMemo(
    () =>
      complaints
        .filter((complaint) => complaint.assignmentStatus === "accepted" && String(complaint.status || "").toLowerCase() !== "completed")
        .map((complaint) => {
          const customer = customerMap.get(String(complaint.customerId));
          const customerAddress = formatAddress(customer?.address);
          const destination =
            customerAddress && customerAddress !== "-"
              ? customerAddress
              : formatAddress(complaint.address) !== "-"
              ? formatAddress(complaint.address)
              : complaint.location || "-";
          const city =
            customer?.address?.city ||
            (complaint.address && typeof complaint.address === "object" ? complaint.address.city : "") ||
            complaint.city ||
            "";
          return {
            ...complaint,
            destination,
            city
          };
        }),
    [complaints, customerMap]
  );

  const selectedComplaint = complaintOptions.find((complaint) => complaint._id === selectedComplaintId);
  const nextAction = getNextAction(travel);

  const createTravel = async () => {
    if (!selectedComplaint) {
      alert("Please select an assigned complaint");
      return;
    }

    try {
      setCreating(true);

      const geo = await getGeolocation();
      const coords = geo ? { latitude: geo.latitude, longitude: geo.longitude } : {};

      await axios.post(API_BASE_URL + "/api/travel", {
        entryType: "complaint",
        engineerId: engineerLookup,
        engineerName,
        complaintId: selectedComplaint._id,
        complaintRefId: selectedComplaint.complaintId,
        customerId: selectedComplaint.customerId,
        company: selectedComplaint.company,
        location: selectedComplaint.destination,
        city: selectedComplaint.city,
        ...coords
      });

      setSelectedComplaintId("");
      await fetchData();
    } catch (error) {
      alert(error.response?.data?.msg || "Failed to create travel");
    } finally {
      setCreating(false);
    }
  };

  const updateStatus = async () => {
    if (!travel?._id || !nextAction) return;

    try {
      setUpdatingStatus(true);
      const geo = await getGeolocation();
      const coords = geo ? { latitude: geo.latitude, longitude: geo.longitude } : {};

      await axios.put(API_BASE_URL + `/api/travel/${travel._id}`, {
        status: nextAction.status,
        timeField: nextAction.field,
        ...coords
      });
      await fetchData();
    } catch (error) {
      alert(error.response?.data?.msg || "Failed to update travel status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <EngineerLayout>
      <div className="mx-auto max-w-6xl space-y-6">
        <section className="rounded-[30px] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.14),_transparent_34%),linear-gradient(135deg,_rgba(255,255,255,1),_rgba(248,250,252,1),_rgba(224,231,255,0.78))] p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-700">Travel Update</p>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">Complaint Travel</h1>
            </div>

            <div className="rounded-2xl border border-sky-100 bg-white/80 px-4 py-3 text-sm text-slate-700">
              Engineer: <span className="font-semibold text-slate-900">{engineerName || engineerLookup || "-"}</span>
            </div>
          </div>
        </section>

        <section className="rounded-[30px] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-end">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-semibold text-slate-700">Assigned Complaint</label>
              <select
                value={selectedComplaintId}
                onChange={(event) => setSelectedComplaintId(event.target.value)}
                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100"
              >
                <option value="">Select complaint</option>
                {complaintOptions.map((complaint) => (
                  <option key={complaint._id} value={complaint._id}>
                    {complaint.complaintId} - {complaint.company} - {complaint.destination}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              onClick={createTravel}
              disabled={!selectedComplaint || creating}
              className="rounded-2xl bg-sky-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-400"
            >
              {creating ? "Adding..." : "Add Travel"}
            </button>
          </div>

          {selectedComplaint && (
            <div className="mt-5 grid gap-4 md:grid-cols-3">
              <TravelField label="Complaint ID" value={selectedComplaint.complaintId} />
              <TravelField label="Customer" value={selectedComplaint.company} />
              <TravelField label="Destination" value={selectedComplaint.destination} />
            </div>
          )}
        </section>

        <section className="overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-slate-50 px-6 py-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Active Travel</h2>
              </div>
              {travel && <StatusBadge status={travel.status} />}
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading travel data...</div>
          ) : !travel ? (
            <div className="p-8 text-center text-slate-500">No active complaint travel found.</div>
          ) : (
            <div className="p-6">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <TravelField label="Complaint ID" value={getComplaintLabel(travel)} />
                <TravelField label="Company" value={travel.company || "-"} />
                <TravelField label="Destination" value={travel.location || "-"} />
                <TravelField label="City" value={travel.city || "-"} />
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <TravelField label="Journey Started" value={formatDateTime(travel.journey?.startTime)} />
                <TravelField label="Reached Site" value={formatDateTime(travel.journey?.reachSiteTime)} />
                <TravelField label="Left Site" value={formatDateTime(travel.journey?.leaveSiteTime)} />
                <TravelField label="Reached Home" value={formatDateTime(travel.journey?.reachHomeTime)} />
              </div>

              {nextAction && (
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={updateStatus}
                    disabled={updatingStatus}
                    className="rounded-2xl bg-emerald-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-400"
                  >
                    {updatingStatus ? "Updating..." : nextAction.label}
                  </button>
                </div>
              )}
            </div>
          )}
        </section>

        <section className="overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 bg-slate-50 px-6 py-4">
            <h2 className="text-lg font-semibold text-slate-900">Travel History</h2>
          </div>

          {travelHistory.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No complaint travel history found.</div>
          ) : (
            <div className="divide-y divide-slate-200">
              {travelHistory.map((item) => (
                <div key={item._id} className="grid gap-4 px-6 py-4 md:grid-cols-[1fr_1fr_1fr_0.8fr] md:items-center">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{getComplaintLabel(item)}</p>
                    <p className="mt-1 text-sm text-slate-500">{item.company || "-"}</p>
                  </div>
                  <div className="text-sm text-slate-600">{item.location || "-"}</div>
                  <div className="text-sm text-slate-600">
                    Start: {formatDateTime(item.journey?.startTime)}
                  </div>
                  <div className="md:text-right">
                    <StatusBadge status={item.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </EngineerLayout>
  );
}

function TravelField({ label, value }) {
  return (
    <div className="rounded-2xl bg-slate-50 p-4">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</p>
      <p className="mt-2 text-sm text-slate-700">{value || "-"}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-sm text-white ${statusBadgeColor(status)}`}>
      {status || "Not Started"}
    </span>
  );
}


